/**
 * MUTAGIM.SHOP — Phase 17: Analytics Dashboard
 * Dashboard principal avec KPIs, graphiques et temps réel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users,
  ShoppingBag, Target, Clock, RefreshCw, ChevronLeft, ChevronRight,
  BarChart3, PieChart, Activity, Zap, Award, Share2, Settings,
  Loader2, ArrowUpRight, ArrowDownRight, Eye, FileDown, Brain
} from 'lucide-react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
}

// Composant pour afficher un KPI
function KPICard({ title, value, change, icon: Icon, format = 'number', loading }) {
  const isPositive = change >= 0;
  
  const formatValue = (val) => {
    if (format === 'currency') return `${val.toLocaleString('fr-FR')}€`;
    if (format === 'percent') return `${val}%`;
    return val.toLocaleString('fr-FR');
  };

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="p-2 bg-mutagim-red/20 rounded-lg">
            <Icon className="w-5 h-5 text-mutagim-red" />
          </div>
          {change !== undefined && (
            <Badge className={cn(
              "text-xs",
              isPositive ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
            )}>
              {isPositive ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
              {Math.abs(change)}%
            </Badge>
          )}
        </div>
        <div className="mt-4">
          <p className="text-sm text-white">{title}</p>
          {loading ? (
            <div className="h-8 w-24 bg-zinc-800 animate-pulse rounded mt-1" />
          ) : (
            <p className="text-2xl font-bold text-white mt-1">{formatValue(value)}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Composant graphique simple en barres
function SimpleBarChart({ data, dataKey, labelKey, color = '#ef4444' }) {
  if (!data || data.length === 0) return <p className="text-gray-200 text-center py-8">Pas de données</p>;
  
  const maxValue = Math.max(...data.map(d => d[dataKey] || 0));
  
  return (
    <div className="space-y-3">
      {data.slice(0, 10).map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="w-32 text-sm text-white truncate">{item[labelKey] || 'N/A'}</span>
          <div className="flex-1 bg-zinc-800 rounded-full h-4 overflow-hidden">
            <div 
              className="h-full rounded-full transition-all"
              style={{ 
                width: `${(item[dataKey] / maxValue) * 100}%`,
                backgroundColor: color
              }}
            />
          </div>
          <span className="w-20 text-sm text-white text-right">
            {typeof item[dataKey] === 'number' ? item[dataKey].toLocaleString('fr-FR') : item[dataKey]}
          </span>
        </div>
      ))}
    </div>
  );
}

export function AdminAnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // States
  const [period, setPeriod] = useState('30days');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [kpis, setKpis] = useState(null);
  const [revenueChart, setRevenueChart] = useState([]);
  const [categoriesChart, setCategoriesChart] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [channelsData, setChannelsData] = useState([]);
  const [realtimeData, setRealtimeData] = useState(null);
  const [crewAnalytics, setCrewAnalytics] = useState(null);
  const [socialAnalytics, setSocialAnalytics] = useState(null);

  const fetchAnalytics = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);
    
    const token = getToken();
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      // Fetch KPIs
      const kpisRes = await fetch(`${API_URL}/api/analytics/kpis?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (kpisRes.ok) {
        const data = await kpisRes.json();
        setKpis(data.kpis);
      }

      // Fetch Revenue Chart
      const revenueRes = await fetch(`${API_URL}/api/analytics/charts/revenue?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (revenueRes.ok) {
        const data = await revenueRes.json();
        setRevenueChart(data.data || []);
      }

      // Fetch Categories
      const catRes = await fetch(`${API_URL}/api/analytics/charts/categories?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (catRes.ok) {
        const data = await catRes.json();
        setCategoriesChart(data.data || []);
      }

      // Fetch Top Products
      const topRes = await fetch(`${API_URL}/api/analytics/charts/top-products?period=${period}&by=revenue`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (topRes.ok) {
        const data = await topRes.json();
        setTopProducts(data.data || []);
      }

      // Fetch Channels
      const channelsRes = await fetch(`${API_URL}/api/analytics/charts/channels?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (channelsRes.ok) {
        const data = await channelsRes.json();
        setChannelsData(data.data || []);
      }

      // Fetch Crew Analytics
      const crewRes = await fetch(`${API_URL}/api/analytics/crew?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (crewRes.ok) {
        const data = await crewRes.json();
        setCrewAnalytics(data);
      }

      // Fetch Social Analytics
      const socialRes = await fetch(`${API_URL}/api/analytics/social?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (socialRes.ok) {
        const data = await socialRes.json();
        setSocialAnalytics(data);
      }
    } catch (error) {
      console.error('Analytics fetch error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, navigate]);

  // Fetch realtime data every 60s
  const fetchRealtime = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_URL}/api/analytics/realtime`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRealtimeData(data);
      }
    } catch (error) {
      console.error('Realtime fetch error:', error);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    fetchRealtime();
    const interval = setInterval(fetchRealtime, 60000);
    return () => clearInterval(interval);
  }, [fetchRealtime]);

  const periods = [
    { value: 'today', label: "Aujourd'hui" },
    { value: '7days', label: '7 jours' },
    { value: '30days', label: '30 jours' },
    { value: '90days', label: '90 jours' },
  ];

  return (
    <AdminPageWrapper title="Analytics Dashboard" subtitle="Performance de votre boutique">
      <div className="max-w-7xl mx-auto" data-testid="admin-analytics-page">
        {/* Filters */}
        <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periods.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={() => fetchAnalytics(false)}
            disabled={refreshing}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
            Actualiser
          </Button>
          
          <Button variant="outline" onClick={() => navigate('/admin/analytics/ab-tests')}>
            <Target className="w-4 h-4 mr-2" />
            A/B Tests
          </Button>
          
          <Button variant="outline" onClick={() => navigate('/admin/analytics/behavior')}>
            <Brain className="w-4 h-4 mr-2" />
            Comportement
          </Button>
          
          <Button 
            variant="outline"
            onClick={async () => {
              const periodMap = { 'today': '7d', '7days': '7d', '30days': '30d', '90days': '90d' };
              const p = periodMap[period] || '30d';
              window.open(`${API_URL}/api/analytics/export/pdf?period=${p}`, '_blank');
            }}
          >
            <FileDown className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="realtime">
              <Activity className="w-4 h-4 mr-2" />
              Temps réel
            </TabsTrigger>
              <TabsTrigger value="crew" className="data-[state=active]:bg-zinc-800">
                <Award className="w-4 h-4 mr-2" />
                Mutagim Crew
              </TabsTrigger>
              <TabsTrigger value="social" className="data-[state=active]:bg-zinc-800">
                <Share2 className="w-4 h-4 mr-2" />
                Social Media
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* KPIs Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <KPICard
                  title="CA Total"
                  value={kpis?.total_revenue || 0}
                  change={kpis?.revenue_change_percent}
                  icon={DollarSign}
                  format="currency"
                  loading={loading}
                />
                <KPICard
                  title="Commandes"
                  value={kpis?.order_count || 0}
                  icon={ShoppingCart}
                  loading={loading}
                />
                <KPICard
                  title="Panier Moyen"
                  value={kpis?.avg_order_value || 0}
                  icon={ShoppingBag}
                  format="currency"
                  loading={loading}
                />
                <KPICard
                  title="Taux Conversion"
                  value={kpis?.conversion_rate || 0}
                  icon={Target}
                  format="percent"
                  loading={loading}
                />
                <KPICard
                  title="Abandon Panier"
                  value={kpis?.cart_abandonment_rate || 0}
                  icon={TrendingDown}
                  format="percent"
                  loading={loading}
                />
                <KPICard
                  title="Sessions"
                  value={kpis?.total_sessions || 0}
                  icon={Users}
                  loading={loading}
                />
              </div>

              {/* Charts Row */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Revenue Chart */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      CA par jour
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="h-48 bg-zinc-800 animate-pulse rounded" />
                    ) : revenueChart.length > 0 ? (
                      <div className="h-48 flex items-end gap-1">
                        {revenueChart.slice(-30).map((day, i) => {
                          const maxRev = Math.max(...revenueChart.map(d => d.revenue || 0));
                          const height = maxRev > 0 ? (day.revenue / maxRev) * 100 : 0;
                          return (
                            <div 
                              key={i}
                              className="flex-1 bg-blue-500/50 hover:bg-blue-500 transition-colors rounded-t"
                              style={{ height: `${Math.max(height, 2)}%` }}
                              title={`${day.date}: ${day.revenue}€`}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-200 text-center py-8">Pas de données</p>
                    )}
                  </CardContent>
                </Card>

                {/* Categories Pie */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-purple-400" />
                      Ventes par catégorie
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="space-y-3">
                        {[1,2,3,4].map(i => <div key={i} className="h-6 bg-zinc-800 animate-pulse rounded" />)}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {categoriesChart.slice(0, 5).map((cat, i) => {
                          const colors = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];
                          return (
                            <div key={i} className="flex items-center gap-3">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[i] }} />
                              <span className="flex-1 text-sm text-zinc-300 truncate">{cat.category}</span>
                              <span className="text-sm text-white font-medium">{cat.percentage}%</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Top Products & Channels */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Top Products */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Award className="w-5 h-5 text-yellow-400" />
                      Top 10 Produits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="space-y-3">
                        {[1,2,3,4,5].map(i => <div key={i} className="h-8 bg-zinc-800 animate-pulse rounded" />)}
                      </div>
                    ) : (
                      <SimpleBarChart 
                        data={topProducts} 
                        dataKey="revenue" 
                        labelKey="name" 
                        color="#ef4444"
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Channels Performance */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Share2 className="w-5 h-5 text-cyan-400" />
                      Performance par canal
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="space-y-3">
                        {[1,2,3,4].map(i => <div key={i} className="h-8 bg-zinc-800 animate-pulse rounded" />)}
                      </div>
                    ) : (
                      <SimpleBarChart 
                        data={channelsData} 
                        dataKey="revenue" 
                        labelKey="channel" 
                        color="#06b6d4"
                      />
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Realtime Tab */}
            <TabsContent value="realtime" className="space-y-6">
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-500/30">
                  <CardContent className="p-6 text-center">
                    <Activity className="w-8 h-8 text-green-400 mx-auto mb-2 animate-pulse" />
                    <p className="text-3xl font-bold text-white">{realtimeData?.active_visitors || 0}</p>
                    <p className="text-sm text-green-300">Visiteurs actifs</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-500/30">
                  <CardContent className="p-6 text-center">
                    <ShoppingCart className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-white">{realtimeData?.active_carts || 0}</p>
                    <p className="text-sm text-blue-300">Paniers actifs</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-yellow-900/50 to-yellow-800/30 border-yellow-500/30">
                  <CardContent className="p-6 text-center">
                    <Clock className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-white">{realtimeData?.processing_orders || 0}</p>
                    <p className="text-sm text-yellow-300">Commandes en cours</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-6 text-center">
                    <RefreshCw className="w-8 h-8 text-white mx-auto mb-2" />
                    <p className="text-xs text-gray-200">Mise à jour toutes les 60s</p>
                    <p className="text-sm text-zinc-300 mt-2">
                      {realtimeData?.timestamp ? new Date(realtimeData.timestamp).toLocaleTimeString('fr-FR') : '-'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Trending Products */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      Produits tendance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {realtimeData?.trending_products?.length > 0 ? (
                      <div className="space-y-3">
                        {realtimeData.trending_products.map((product, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded">
                            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center text-sm">
                              {i + 1}
                            </span>
                            <span className="flex-1 text-white">{product.name}</span>
                            <Badge>{product.views} vues</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-200 text-center py-8">Pas de données en temps réel</p>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Orders */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <ShoppingBag className="w-5 h-5 text-green-400" />
                      Dernières commandes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {realtimeData?.recent_orders?.length > 0 ? (
                      <div className="space-y-3">
                        {realtimeData.recent_orders.map((order, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded">
                            <div>
                              <span className="text-white font-mono">#{order.order_number}</span>
                              <p className="text-xs text-white">
                                {order.created_at ? new Date(order.created_at).toLocaleTimeString('fr-FR') : '-'}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-green-400 font-bold">{order.total}€</span>
                              <Badge className="ml-2" variant={order.status === 'paid' ? 'default' : 'secondary'}>
                                {order.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-200 text-center py-8">Aucune commande récente</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Crew Tab */}
            <TabsContent value="crew" className="space-y-6">
              {/* Crew KPIs */}
              <div className="grid md:grid-cols-4 gap-4">
                <KPICard
                  title="CA Crew"
                  value={crewAnalytics?.crew_stats?.total_revenue || 0}
                  icon={DollarSign}
                  format="currency"
                  loading={loading}
                />
                <KPICard
                  title="Commandes Crew"
                  value={crewAnalytics?.crew_stats?.order_count || 0}
                  icon={ShoppingCart}
                  loading={loading}
                />
                <KPICard
                  title="Commissions"
                  value={crewAnalytics?.crew_stats?.total_commission || 0}
                  icon={TrendingUp}
                  format="currency"
                  loading={loading}
                />
                <KPICard
                  title="ROI Programme"
                  value={crewAnalytics?.crew_stats?.roi_percent || 0}
                  icon={Target}
                  format="percent"
                  loading={loading}
                />
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Top Members */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Award className="w-5 h-5 text-yellow-400" />
                      Meilleurs membres du mois
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {crewAnalytics?.top_members?.length > 0 ? (
                      <div className="space-y-3">
                        {crewAnalytics.top_members.map((member, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center font-bold",
                              i === 0 ? "bg-yellow-500 text-black" :
                              i === 1 ? "bg-zinc-400 text-black" :
                              i === 2 ? "bg-amber-700 text-white" :
                              "bg-zinc-700 text-white"
                            )}>
                              {i + 1}
                            </div>
                            <div className="flex-1">
                              <p className="text-white font-medium">{member.name}</p>
                              <p className="text-xs text-white">{member.ref_code}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-green-400 font-bold">{member.revenue}€</p>
                              <p className="text-xs text-white">{member.orders} ventes</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-200 text-center py-8">Pas de données Crew</p>
                    )}
                  </CardContent>
                </Card>

                {/* Monthly Evolution */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-400" />
                      Évolution CA Crew
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {crewAnalytics?.monthly_evolution?.length > 0 ? (
                      <SimpleBarChart 
                        data={crewAnalytics.monthly_evolution} 
                        dataKey="revenue" 
                        labelKey="month" 
                        color="#8b5cf6"
                      />
                    ) : (
                      <p className="text-gray-200 text-center py-8">Pas de données historiques</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Social Tab */}
            <TabsContent value="social" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <KPICard
                  title="Posts créés"
                  value={socialAnalytics?.stats?.posts_created || 0}
                  icon={Share2}
                  loading={loading}
                />
                <KPICard
                  title="Posts publiés"
                  value={socialAnalytics?.stats?.posts_published || 0}
                  icon={Zap}
                  loading={loading}
                />
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* By Platform */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">Publications par réseau</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {socialAnalytics?.by_platform?.length > 0 ? (
                      <SimpleBarChart 
                        data={socialAnalytics.by_platform} 
                        dataKey="posts" 
                        labelKey="platform" 
                        color="#ec4899"
                      />
                    ) : (
                      <p className="text-gray-200 text-center py-8">Aucune publication</p>
                    )}
                  </CardContent>
                </Card>

                {/* Top Visuals */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-white">Dernières publications</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin/social/history')}>
                      <Eye className="w-4 h-4 mr-2" />
                      Voir tout
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {socialAnalytics?.top_visuals?.length > 0 ? (
                      <div className="space-y-3">
                        {socialAnalytics.top_visuals.map((visual, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 bg-zinc-800/50 rounded">
                            <span className="text-white flex-1 truncate">{visual.product_name}</span>
                            <Badge variant="secondary">{visual.format}</Badge>
                            <div className="flex gap-1">
                              {visual.platforms?.map((p, j) => (
                                <Badge key={j} className="text-xs">{p}</Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-200 text-center py-8">Aucune publication</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
      </div>
    </AdminPageWrapper>
  );
}

export default AdminAnalyticsPage;
